<script setup lang="ts">
// Reactions list is rendered inside PostViewBody
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { UserState } from '@/composition/users'
import { DEFAULT_SAFE_AREA_INSET, Platform } from '@/helper/const'
import { IComments } from '@/models/Comments'
import { GroupMemberRole } from '@/models/GroupMember'
import { IPosts } from '@/models/Posts'
import { useGlobalStore } from '@/store/app'
import {
  Capacitor,
  registerPlugin,
  type PluginListenerHandle,
} from '@capacitor/core'
import { MobileScrollContainer } from '@ghl/clientportal-core'
import { UIModal, UIModalHeader } from '@gohighlevel/ghl-ui'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  watch,
} from 'vue'

import { useRoute, useRouter } from 'vue-router'
import { emitter } from '../../../main'

import {
  checkNativeKeyboardResize,
  handleEditorVisibility,
} from '@/util/helper'

import PostViewBody from './PostViewBody.vue'
import PostViewFooter from './PostViewFooter.vue'
import PostViewHeader from './PostViewHeader.vue'

const KeyboardEventsPlugin: any = registerPlugin('KeyboardEvents')
let keyboardWillShowListener: PluginListenerHandle | null = null
let keyboardWillHideListener: PluginListenerHandle | null = null

const appStore = useGlobalStore()

// Props from PostViewRouter (presentational modal wrapper)
const props = defineProps({
  post: { type: Object as PropType<IPosts>, required: false },
  sanitizedContent: { type: String, required: false },
  authorRole: { type: String as PropType<GroupMemberRole>, required: false },
  channelName: { type: String, required: false },
  comments: { type: Array as PropType<IComments[]>, required: false },
  fetchingComments: { type: Boolean, required: false },
  loadingMoreComments: { type: Boolean, required: false },
  commentsNextPage: { type: Boolean, required: false },
  showLikesModal: { type: Boolean, required: false },
  postActionOptions: {
    type: Array as () => Array<{
      label: string
      key: string
      class: string
      icon: () => any
    }>,
    required: false,
  },
})

const emit = defineEmits<{
  (e: 'like'): void
  (e: 'load-more-comments'): void
  (e: 'likes-clicked'): void
  (e: 'action-selected', action: string): void
  (e: 'open-join-group-modal'): void
}>()

// lazy load components
const AttachmentsPreview = defineAsyncComponent(
  () => import('@/components/common/AttachmentsPreview.vue')
)

const CommentEditor = defineAsyncComponent(
  () => import('@/components/comments/CommentEditor.vue')
)

const router = useRouter()
const route = useRoute()
const showModal = ref(true)

const contentContainer = ref<HTMLElement | null>(null)
const showCommentEditor = ref(true)
const isContentScrolled = ref(false)
const isCommentAReply = ref(false)

// Add event listener to show footer comment editor when reply editors are closed
emitter.on('show:footer-comment-editor', () => {
  showCommentEditor.value = true
})

const commentEditorBottom = ref(0)

const handlePostViewBack = () => {
  showModal.value = false
}

onMounted(async () => {
  emitter.on('close-postview', () => (showModal.value = false))
  const hasNativeKeyboardResize = await checkNativeKeyboardResize(
    KeyboardEventsPlugin
  )

  if (Capacitor.getPlatform() === Platform.IOS && !hasNativeKeyboardResize) {
    keyboardWillShowListener = await KeyboardEventsPlugin.addListener(
      'keyboardWillShow',
      handleKeyboardWillShowEvent
    )

    keyboardWillHideListener = await KeyboardEventsPlugin.addListener(
      'keyboardWillHide',
      handleKeyboardWillHideEvent
    )
  }

  // Add back button handler
  window.addEventListener('popstate', handlePostViewBack)

  contentContainer.value?.addEventListener('scroll', handleContentScroll)
  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.overlaysContent = true
    // @ts-ignore
    navigator.virtualKeyboard.addEventListener(
      'geometrychange',
      handleGeometryChange
    )
  } else if (Capacitor.getPlatform() !== Platform.IOS) {
    // Handle layout changes in case virtualkeyboard API is not supported
    window.visualViewport?.addEventListener(
      'resize',
      handleVisualViewportResize
    )
  }

  nextTick(() => {
    // Only setup intersection observer for mobile
    if (app.state.isMobile) {
      const intercept = document.querySelector('[data-scroll-intercept]')
      const header = document.getElementById('post_view_top__container')

      if (intercept && header) {
        const observer = new IntersectionObserver(([entry]) => {
          header.classList.toggle('shadow-md', !entry.isIntersecting)
        })
        observer.observe(intercept)
      }
    }
  })
  emitter.on('close:all-comment-editors', () => {
    showCommentEditor.value = false
  })
})

function handleGeometryChange(changeEvent) {
  const { height } = changeEvent.target.boundingRect
  commentEditorBottom.value = height
}

function handleVisualViewportResize() {
  if (app.state.isMobile) {
    if (
      window.visualViewport &&
      window.visualViewport?.height < window.innerHeight
    ) {
      // Keyboard likely is open.
      /**
       * Set Max height of the content to visual viewport size
       * Counter scroll to the top to negate safari webkit scroll due to input focus.
       * And then set the commment editor to the bottom of the visual viewport
       */
      const keyboardHeight = window.innerHeight - window.visualViewport.height

      if (!isCommentAReply.value) {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
            left: 0,
          })
          commentEditorBottom.value = keyboardHeight
        }, 50)
      } else {
        isCommentAReply.value = false
      }
      document.body.style.position = 'fixed'
      document.body.style.overflow = 'hidden'
      const postViewModalContent = document.querySelector(
        '.post-view-modal'
      ) as HTMLElement
      const postContent = document.getElementById(
        'post-view-modal-content'
      ) as HTMLElement
      if (postViewModalContent && window.visualViewport) {
        postViewModalContent.style.maxHeight = `${window.visualViewport.height}px`
        postContent.style.maxHeight = `${window.visualViewport.height}px`
      }
      isCommentAReply.value = false // Reset
    } else if (window.visualViewport?.height == window.innerHeight) {
      // Keyboard likely is closed.
      commentEditorBottom.value = 0
      const postViewModalContent = document.querySelector(
        '.post-view-modal'
      ) as HTMLElement
      const postContent = document.getElementById(
        'post-view-modal-content'
      ) as HTMLElement
      if (postViewModalContent && window.visualViewport) {
        postViewModalContent.style.maxHeight = '100%'
        postContent.style.maxHeight = '100%'
      }
      isCommentAReply.value = false //reset
      document.body.style.position = ''
      document.body.style.overflow = ''
    }
  }
}

function handleKeyboardWillShow(keyboardHeight) {
  // Add a small delay to ensure DOM is ready
  setTimeout(() => {
    if (!isCommentAReply.value) {
      commentEditorBottom.value = keyboardHeight - DEFAULT_SAFE_AREA_INSET

      // Force layout recalculation
      document.body.style.position = 'fixed'
      document.body.style.overflow = 'hidden'

      const postViewModalContent = document.querySelector(
        '.post-view-modal'
      ) as HTMLElement
      const postContent = document.getElementById(
        'post-view-modal-content'
      ) as HTMLElement

      if (postViewModalContent && window.visualViewport) {
        postViewModalContent.style.maxHeight = `${window.visualViewport.height}px`
        postContent.style.maxHeight = `${window.visualViewport.height}px`
      }

      // Force redraw

      postViewModalContent?.offsetHeight

      postContent?.offsetHeight

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
        left: 0,
      })
    }
    isCommentAReply.value = false // Reset
  }, 100)
}

function handleKeyboardWillHide() {
  // Add a small delay to ensure proper state cleanup
  setTimeout(() => {
    commentEditorBottom.value = 0

    const postViewModalContent = document.querySelector(
      '.post-view-modal'
    ) as HTMLElement
    const postContent = document.getElementById(
      'post-view-modal-content'
    ) as HTMLElement

    if (postViewModalContent && window.visualViewport) {
      postViewModalContent.style.maxHeight = '100%'
      postContent.style.maxHeight = '100%'
    }

    // Force redraw

    postViewModalContent?.offsetHeight

    postContent?.offsetHeight

    document.body.style.position = ''
    document.body.style.overflow = 'scroll'
    document.body.style.overflowY = 'scroll'

    isCommentAReply.value = false //reset
  }, 100)
}

function handleKeyboardWillShowEvent(info: { keyboardHeight: number }) {
  handleKeyboardWillShow(info.keyboardHeight)
}

function handleKeyboardWillHideEvent() {
  handleKeyboardWillHide()
}

function removeKeyboardEventListeners() {
  if (typeof KeyboardEventsPlugin.removeEventListener === 'function') {
    KeyboardEventsPlugin.removeEventListener(
      'keyboardWillShow',
      handleKeyboardWillShowEvent
    )
    KeyboardEventsPlugin.removeEventListener(
      'keyboardWillHide',
      handleKeyboardWillHideEvent
    )
  } else {
    void keyboardWillShowListener?.remove()
    void keyboardWillHideListener?.remove()
  }

  keyboardWillShowListener = null
  keyboardWillHideListener = null
}

function handleContentScroll() {
  isContentScrolled.value =
    contentContainer?.value && contentContainer?.value?.scrollTop > 0
      ? true
      : false
}

watch(
  () => props.fetchingComments,
  newValue => {
    if (newValue === false) {
      if (route.query?.commentId) {
        // reorder the highlighted comment to top of list of comments
        // handled upstream in router
        nextTick(() => {
          // Code to execute after rendering

          const commentsCards = document.getElementById('comments_cards')
          if (commentsCards) {
            setTimeout(() => {
              commentsCards.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
            }, 150)
          }
        })
      }
    }
  }
)

watch(
  () => showModal.value,
  () => {
    handleEditorVisibility(showModal.value)
  },
  { immediate: true }
)

onUnmounted(() => {
  emitter.off('close-postview')
  handleEditorVisibility(false)
  setTimeout(() => {
    document.body.style.removeProperty('position')
    document.body.style.overflow = ''
  }, 100)
  window.visualViewport?.removeEventListener(
    'resize',
    handleVisualViewportResize
  )
})

onBeforeUnmount(() => {
  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.removeEventListener(
      'geometrychange',
      handleGeometryChange
    )
  }
  contentContainer.value?.removeEventListener('scroll', handleContentScroll)

  // Remove event listeners
  window.removeEventListener('popstate', handlePostViewBack)
  emitter.off('close:all-comment-editors')
  emitter.off('show:footer-comment-editor')
  removeKeyboardEventListeners()
})

function loadMoreComments() {
  emit('load-more-comments')
}

function closeModal() {
  handleEditorVisibility(false)
  document.body.style.position = ''
  showModal.value = false
}

const isEditorVisibleToUser = computed(() => {
  return appStore.isAuthenticated && GroupState.isActiveMember()
})

watch(
  () => showModal.value,
  newValue => {
    if (newValue === false) {
      emitter.emit('close-postview')
    } else {
      // Reset comment editor state when modal opens
      showCommentEditor.value = true
    }
  }
)

watch(
  () => showModal.value,
  value => {
    if (value) {
      // Mark navigation-initiated open
      window.history.replaceState(
        { ...(window.history.state || {}), modal: true },
        ''
      )
    }
  }
)

function likePost() {
  emit('like')
}

const authorRole = computed(() => {
  return GroupState.state.groupAdmins.get(props.post?.user.contactId)?.role
})

const showPostPreview = ref(false)
const currentPostIndex = ref(0)

function updatePreview(index: number) {
  currentPostIndex.value = index
  showPostPreview.value = true
}

function handlePostAction(val: string) {
  emit('action-selected', val)
}

function handleChannelClick(slug: string) {
  router.push({
    name: 'channel-overview',
    params: { channelSlug: slug },
  })
}
</script>
<template>
  <div
    class="modal-parent sticky top-0 flex w-full select-none overflow-hidden"
  >
    <UIModal
      v-if="post?._id"
      :show="showModal"
      class-name="post-view-modal flex h-full w-full rounded-none md:rounded-xl md:max-h-[90vh] md:h-auto absolute top-[58px] left-0 right-0"
      :auto-focus="true"
      :z-index="2000"
      :width="791"
      @update:show="closeModal"
      @on-esc="closeModal"
      @on-mask-click="closeModal"
      @touchmove="
        event => {
          event.preventDefault()
        }
      "
    >
      <template #header>
        <UIModalHeader id="modal-header" ref="postViewHeader" type="custom">
          <template #customHeader>
            <PostViewHeader
              :post="post"
              :channel-slug="post?.channelSlug"
              :author-role="authorRole"
              :channel-name="post?.channelName"
              :post-action-options="postActionOptions"
              :is-content-scrolled="isContentScrolled"
              @back="closeModal"
              @channel-click="handleChannelClick"
              @action-selected="handlePostAction"
            />
          </template>
        </UIModalHeader>
      </template>
      <div id="post-view-modal-content" class="flex flex-col overflow-hidden">
        <div
          ref="contentContainer"
          class="flex flex-col overflow-y-auto md:min-h-0"
          :class="{
            'pt-[61px]': app.state.isMobile,
            'flex-1': app.state.isMobile,
          }"
          @scroll="handleContentScroll"
        >
          <MobileScrollContainer>
            <template #scroll-slot>
              <PostViewBody
                :post="post"
                :comment-editor-bottom="commentEditorBottom"
                :comments="props.comments"
                :fetching-comments="props.fetchingComments"
                :loading-more-comments="props.loadingMoreComments"
                :comments-next-page="props.commentsNextPage"
                :show-likes-modal="showLikesModal"
                @likes-clicked="() => emit('likes-clicked')"
                @update-preview="updatePreview"
                @like="likePost"
                @load-more-comments="loadMoreComments"
                @open-join-group-modal="emit('open-join-group-modal')"
              />

              <PostViewFooter class="sticky bottom-0 bg-communities-background">
                <template #comment-editor>
                  <CommentEditor
                    v-if="showCommentEditor && isEditorVisibleToUser"
                    :comment-editor-bottom="commentEditorBottom"
                    :user="UserState.state.currentUser"
                  />
                </template>
              </PostViewFooter>
              <!-- Attach mention handlers on rendered HTML -->
              <div v-if="post" class="hidden" />
            </template>
          </MobileScrollContainer>
        </div>
      </div>
      <AttachmentsPreview
        :show-modal="showPostPreview"
        :file="post?.attachments && post.attachments[currentPostIndex]"
        :current-index="currentPostIndex"
        :total-attachments="post?.attachments?.length"
        @update:close="showPostPreview = false"
        @update:show="updatePreview"
      />
    </UIModal>
  </div>
</template>
<style>
.custom-modal-post-view-width {
  min-width: 375px;
  max-width: 848px;
  width: auto !important;
}
a:active,
a:focus {
  outline: 0;
  border: none;
  -moz-outline-style: none;
}
</style>
<style scoped>
#post_view_top__container {
  background: var(--communities-background);
}
.backbutton [tabindex='0']:focus {
  outline: none;
  box-shadow: none;
}

/* Contain modal inner content and enable internal scroll */
#post-view-modal-content {
  display: flex;
  flex-direction: column;
  max-height: calc(90vh - 100px); /* Account for modal header */
  overflow: hidden;
}

@media (max-width: 930px) {
  #post-view-modal-content {
    height: 100vh;
    max-height: 100vh;
  }
}

@media (max-width: 930px) {
  #post_view_top__container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background: white;
    border-bottom: 0.75px solid var(--border);
  }

  #post_view_top__container::before {
    content: '';
    position: absolute;
    inset: 0;
    background: white;
    z-index: -1;
  }
}
</style>
<style>
.post-view-modal {
  max-width: 791px !important;
  width: 80% !important;
  padding: 24px 12px !important;
}
@media (max-width: 930px) {
  .post-view-modal {
    position: fixed !important;
    width: 100% !important;
    top: 0px !important;
    left: 0px !important;
    right: 0px !important;
    overflow-y: scroll !important;
    padding: 0px !important;
    max-width: 100% !important;
  }

  #post-view-modal-content {
    overflow-y: scroll !important;
    max-height: 100vh;
  }
}
</style>
